<template>
  <q-dialog v-model="showDialog" full-width>
    <q-card>
      <q-toolbar>
        <q-toolbar-title>JSON Diff</q-toolbar-title>
        <q-btn flat round icon="close" @click="showDialog = false" />
      </q-toolbar>
      <q-card-section class="q-pa-none diff-container">
        <div class="row no-wrap full-height">
          <div class="col-6 diff-panel">
            <div class="diff-header text-weight-bold q-pa-sm bg-red-1">Before</div>
            <pre class="diff-content" ref="beforePanel" @scroll="syncScroll('before')"><template v-for="(line, index) in diffLines" :key="'before-' + index"><span :class="line.beforeClass" class="diff-line">{{ line.before }}</span></template></pre>
          </div>
          <div class="col-6 diff-panel">
            <div class="diff-header text-weight-bold q-pa-sm bg-green-1">After</div>
            <pre class="diff-content" ref="afterPanel" @scroll="syncScroll('after')"><template v-for="(line, index) in diffLines" :key="'after-' + index"><span :class="line.afterClass" class="diff-line">{{ line.after }}</span></template></pre>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
export default {
  name: 'JsonDiffViewer',
  props: {
    before: {
      type: [Object, Array, String],
      default: () => ({}),
    },
    after: {
      type: [Object, Array, String],
      default: () => ({}),
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      scrolling: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    diffLines() {
      const beforeStr = typeof this.before === 'string' ? this.before : JSON.stringify(this.before, null, 2);
      const afterStr = typeof this.after === 'string' ? this.after : JSON.stringify(this.after, null, 2);

      const beforeLines = beforeStr.split('\n');
      const afterLines = afterStr.split('\n');

      const lcs = this.computeLCS(beforeLines, afterLines);
      return this.buildDiffFromLCS(beforeLines, afterLines, lcs);
    },
  },
  methods: {
    syncScroll(source) {
      if (this.scrolling) return;
      this.scrolling = true;

      const sourceEl = this.$refs[source + 'Panel'];
      const targetEl = this.$refs[source === 'before' ? 'afterPanel' : 'beforePanel'];

      if (sourceEl && targetEl) {
        targetEl.scrollTop = sourceEl.scrollTop;
        targetEl.scrollLeft = sourceEl.scrollLeft;
      }

      requestAnimationFrame(() => {
        this.scrolling = false;
      });
    },

    computeLCS(a, b) {
      const m = a.length;
      const n = b.length;
      const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          if (a[i - 1] === b[j - 1]) {
            dp[i][j] = dp[i - 1][j - 1] + 1;
          } else {
            dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
          }
        }
      }

      const result = [];
      let i = m;
      let j = n;

      while (i > 0 && j > 0) {
        if (a[i - 1] === b[j - 1]) {
          result.unshift({ beforeIdx: i - 1, afterIdx: j - 1 });
          i--;
          j--;
        } else if (dp[i - 1][j] > dp[i][j - 1]) {
          i--;
        } else {
          j--;
        }
      }

      return result;
    },

    buildDiffFromLCS(beforeLines, afterLines, lcs) {
      const lines = [];
      let bIdx = 0;
      let aIdx = 0;

      for (const match of lcs) {
        while (bIdx < match.beforeIdx) {
          lines.push({
            before: beforeLines[bIdx],
            after: '',
            beforeClass: 'bg-red-2',
            afterClass: 'empty-line',
          });
          bIdx++;
        }

        while (aIdx < match.afterIdx) {
          lines.push({
            before: '',
            after: afterLines[aIdx],
            beforeClass: 'empty-line',
            afterClass: 'bg-green-2',
          });
          aIdx++;
        }

        lines.push({
          before: beforeLines[bIdx],
          after: afterLines[aIdx],
          beforeClass: '',
          afterClass: '',
        });

        bIdx++;
        aIdx++;
      }

      while (bIdx < beforeLines.length) {
        lines.push({
          before: beforeLines[bIdx],
          after: '',
          beforeClass: 'bg-red-2',
          afterClass: 'empty-line',
        });
        bIdx++;
      }

      while (aIdx < afterLines.length) {
        lines.push({
          before: '',
          after: afterLines[aIdx],
          beforeClass: 'empty-line',
          afterClass: 'bg-green-2',
        });
        aIdx++;
      }

      return lines;
    },
  },
};
</script>

<style lang="scss" scoped>
.diff-container {
  height: calc(100vh - 100px);
  overflow: hidden;
}

.diff-panel {
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:last-child {
    border-right: none;
  }
}

.diff-header {
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.diff-content {
  margin: 0;
  overflow: auto;
  flex: 1;
  font-size: 12px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  line-height: 1;
  padding: 4px 0;
}

.diff-line {
  display: block;
  padding: 0 8px;
  min-height: 14px;
  white-space: pre;
}

.empty-line {
  background-color: #f5f5f5;
  min-height: 14px;
}

.bg-red-2 {
  background-color: #ffcdd2;
}

.bg-green-2 {
  background-color: #c8e6c9;
}
</style>